import { apiConfig } from "~/configs/apiConfig";
import * as Sentry from "@sentry/remix";

interface RequestConfig {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  data?: any;
}

const IGNORE_ERROR_FOR_STATUSES: number[] = [404, 401];

class ApiError extends Error {
  errorType?: string;
  userMessage?: string;
  info?: string;
  error?: string;
  parsedResponse?: any;
  requestConfig: RequestConfig;

  constructor(
    response: Response,
    responseBody: string,
    requestConfig: RequestConfig,
  ) {
    // Try parse the response body as json, otherwise use some defaults for error information
    let parsedResponse;
    try {
      parsedResponse = JSON.parse(responseBody);
    } catch {
      parsedResponse = null;
    }

    const errorMessage: any =
      parsedResponse?.errorType ??
      parsedResponse?.error ??
      parsedResponse?.message ??
      parsedResponse?.userMessage ??
      "Unexpected Server Error";

    super(errorMessage);

    this.name = `${requestConfig.method} ${requestConfig.url} returned status ${response.status}`;

    this.parsedResponse = parsedResponse;
    this.errorType = parsedResponse?.errorType;
    this.userMessage = parsedResponse?.userMessage;
    this.info = parsedResponse?.info;
    this.error = parsedResponse?.error;
    this.requestConfig = requestConfig;
  }
}

const request = async <T>(config: RequestConfig): Promise<T> => {
  const headers: Record<string, string> = {
    ...config.headers,
    "Content-Type": "application/json",
  };

  // In Remix with Vite, we can use import.meta.env.SSR to detect server environment
  const baseUrl = import.meta.env.SSR
    ? apiConfig.ServerApiUrl
    : apiConfig.ClientApiUrl;

  let response;
  try {
    response = await fetch(`${baseUrl}${config.url}`, {
      method: config.method,
      headers,
      body: config.data ? JSON.stringify(config.data) : undefined,
    });
  } catch (e: any) {
    Sentry.withScope((scope) => {
      scope.setLevel("error");
      scope.setTag("api_error", true);
      scope.setTag("status", e.status);
      scope.captureException(e);
    });
    throw e;
  }

  if (!response.ok) {
    if (IGNORE_ERROR_FOR_STATUSES.includes(response.status)) {
      throw response;
    }

    // Convert response body to text
    const responseBody = await response.text();

    const error = new ApiError(response, responseBody, config);

    // Function name sets event name in sentry
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setTag("api_error", true);
      scope.setTag("status", response.status);
      scope.setTag("info", error.info);
      scope.setTag("user_message", error.userMessage);
      scope.setTag("error_type", error.errorType);
      scope.setTag("error", error.error);
      scope.setTag("message", error.message);
      scope.setTag("response_body", responseBody.replaceAll("\n", ""));
      scope.setContext("Parsed Error", error.parsedResponse);
      scope.captureException(error);
    });

    // Throw the API error
    throw error;
  }

  const hasBody =
    response.headers.get("content-length") !== "0" &&
    response.headers.get("content-type")?.includes("application/json");

  const data = hasBody ? await response.json() : null;
  return data ? data.data : data;
};

const instance = {
  get: <T>(
    url: string,
    config: Omit<RequestConfig, "url" | "method"> = {},
  ): Promise<T> => {
    return request({ url, method: "GET", ...config });
  },

  post: <T>(
    url: string,
    data: any,
    config: Omit<RequestConfig, "url" | "method" | "data"> = {},
  ): Promise<T> => {
    return request({ url, method: "POST", data, ...config });
  },

  put: <T>(
    url: string,
    data: any,
    config: Omit<RequestConfig, "url" | "method" | "data"> = {},
  ): Promise<T> => {
    return request({ url, method: "PUT", data, ...config });
  },
};

export default instance;
